// src/pages/supplier/ProductForm.jsx
import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Upload } from 'lucide-react'
import { supplierProductsApi, categoriesApi } from '../../api/services'
import { Card, CardHeader, CardBody, Button, PageSpinner } from '../../components/common/ui'
import toast from 'react-hot-toast'

const INIT = {
  name: '', description: '', category_id: '', fabric_type: '', composition: '', gsm: '', width_inches: '',
  weave_type: '', pattern: '', origin_state: '', care_instructions: '',
  finish_type: '', shrinkage_pct: '', pilling_grade: '', color_fastness: '', tensile_strength: '',
  drape_coefficient: '', moisture_absorption: '', opacity: '', stretch_pct: '', stretch_direction: '',
  dyeing_method: '', printing_method: '', loom_type: '', selvage_type: '', weight_per_metre: '',
  certification: '', eco_friendly: false, recyclable: false,
  seasonal_suitability: '', recommended_use: '',
  retail_price: '', wholesale_price: '', mrp: '', gst_rate: '5',
  min_order_retail: '1', min_order_wholesale: '50',
  is_wholesale_only: false, is_sample_available: true, is_gi_tagged: false, gi_tag_name: '',
  meta_title: '', meta_description: '',
}

const VARIANT_INIT = { color_name: '', color_hex: '', sku: '', stock_metres: '' }
const TIER_INIT = { min_metres: '', max_metres: '', price_per_metre: '' }

const FABRIC_TYPES = ['Cotton', 'Silk', 'Linen', 'Wool', 'Polyester', 'Rayon', 'Georgette', 'Chiffon', 'Velvet', 'Jute', 'Khadi', 'Chanderi', 'Banarasi', 'Kanjivaram', 'Tussar', 'Other']
const PATTERNS = ['Solid', 'Printed', 'Embroidered', 'Woven', 'Striped', 'Checks', 'Floral', 'Abstract', 'Ikat', 'Batik', 'Block Print', 'Digital Print']
const STATES = ['Andhra Pradesh', 'Assam', 'Bihar', 'Gujarat', 'Karnataka', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Odisha', 'Rajasthan', 'Tamil Nadu', 'Telangana', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Other']

const FINISH_TYPES = ['Mercerised', 'Sanforised', 'Calendered', 'Enzyme-washed', 'Brushed', 'Peached', 'Sueded', 'Bio-polished', 'Stone-washed', 'Other']
const DYEING_METHODS = ['Yarn-dyed', 'Piece-dyed', 'Vat-dyed', 'Reactive', 'Natural dye', 'Indigo', 'Discharge', 'Tie-dye', 'Space-dyed']
const PRINTING_METHODS = ['Screen print', 'Block print', 'Digital print', 'Discharge print', 'Resist print', 'Roller print', 'Rotary print', 'Transfer print']
const LOOM_TYPES = ['Handloom', 'Powerloom', 'Jacquard', 'Dobby', 'Pit loom', 'Frame loom', 'Shuttle loom', 'Rapier']
const OPACITY_OPTIONS = ['Sheer', 'Semi-sheer', 'Semi-opaque', 'Opaque']
const STRETCH_DIRS = ['None', '2-way', '4-way', 'Warp only', 'Weft only']
const DRAPE_OPTIONS = ['Low', 'Medium', 'High', 'Very High']
const SEASONS = ['Summer', 'Winter', 'Monsoon', 'All-season']
const CERTIFICATIONS = ['GOTS', 'OEKO-TEX Standard 100', 'BIS', 'Handloom Mark', 'Silk Mark', 'Woolmark', 'Fair Trade', 'GRS', 'None']
const RECOMMENDED_USES = ['Saree', 'Kurta/Kurti', 'Shirting', 'Suiting', 'Dress material', 'Upholstery', 'Curtains', 'Lehenga', 'Dupatta', 'Stole/Shawl', 'Home linen', 'Quilting']

export default function ProductForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const isEdit = !!id

  const [form, setForm] = useState(INIT)
  const [variants, setVariants] = useState([{ ...VARIANT_INIT }])
  const [tiers, setTiers] = useState([])
  const [images, setImages] = useState([])
  const [tab, setTab] = useState('basic')
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))

  const { data: catData } = useQuery({ queryKey: ['categories'], queryFn: () => categoriesApi.list() })
  const { data: prodData, isLoading: loadingProduct } = useQuery({ queryKey: ['supplier-product', id], queryFn: () => supplierProductsApi.show(id), enabled: isEdit })

  useEffect(() => {
    if (prodData) {
      const p = prodData?.data?.data || prodData?.data
      setForm({ ...INIT, ...p, category_id: p.category_id || p.category?.id || '' })
      setVariants(p.variants?.length ? p.variants : [{ ...VARIANT_INIT }])
      setTiers(p.bulk_pricing_tiers || [])
    }
  }, [prodData])

  const save = useMutation({
    mutationFn: () => {
      const fd = new FormData()
      Object.entries(form).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) fd.append(k, v) })
      fd.append('variants', JSON.stringify(variants.filter(v => v.color_name)))
      fd.append('bulk_pricing_tiers', JSON.stringify(tiers.filter(t => t.min_metres && t.price_per_metre)))
      images.forEach((img) => { if (img instanceof File) fd.append('images[]', img) })
      return isEdit ? supplierProductsApi.update(id, fd) : supplierProductsApi.store(fd)
    },
    onSuccess: (res) => {
      const pid = res?.data?.data?.id || id
      toast.success(isEdit ? 'Product updated!' : 'Product created!')
      qc.invalidateQueries(['supplier-products'])
      navigate('/supplier/products')
    },
    onError: e => toast.error(e.response?.data?.message || Object.values(e.response?.data?.errors || {})[0]?.[0] || 'Save failed'),
  })

  if (isEdit && loadingProduct) return <PageSpinner />
  const categories = catData?.data?.data || []

  const TABS = ['basic', 'details', 'specs', 'pricing', 'variants', 'seo']

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-display font-bold">{isEdit ? 'Edit Product' : 'Add Product'}</h1>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/supplier/products')}>Cancel</Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save Product'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border mb-6 overflow-x-auto">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-bold capitalize border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t ? 'border-pink text-pink' : 'border-transparent text-muted hover:text-gray-700'}`}>
            {t}
          </button>
        ))}
      </div>

      {/* Basic */}
      {tab === 'basic' && (
        <div className="space-y-4">
          <Card>
            <CardHeader><span className="font-bold text-sm">Product Info</span></CardHeader>
            <CardBody className="space-y-3">
              <input className="input w-full" placeholder="Product name *" value={form.name} onChange={set('name')} />
              <textarea className="input w-full h-28" placeholder="Description *" value={form.description} onChange={set('description')} />
              <div className="grid sm:grid-cols-2 gap-3">
                <select className="input" value={form.category_id} onChange={set('category_id')}>
                  <option value="">Select category *</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}{c.parent ? ` (${c.parent.name})` : ''}</option>)}
                </select>
                <select className="input" value={form.fabric_type} onChange={set('fabric_type')}>
                  <option value="">Fabric type</option>
                  {FABRIC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <select className="input" value={form.origin_state} onChange={set('origin_state')}>
                  <option value="">Origin state</option>
                  {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select className="input" value={form.pattern} onChange={set('pattern')}>
                  <option value="">Pattern</option>
                  {PATTERNS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <input className="input" placeholder="Composition (e.g. 60% silk)" value={form.composition} onChange={set('composition')} />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader><span className="font-bold text-sm">Product Images</span></CardHeader>
            <CardBody>
              <div className="grid grid-cols-4 gap-3">
                {images.map((img, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-border group">
                    <img src={img instanceof File ? URL.createObjectURL(img) : (img.url || img)} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setImages(imgs => imgs.filter((_, idx) => idx !== i))}
                      className="absolute top-1 right-1 p-1 bg-white rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 size={10} className="text-red-500" />
                    </button>
                  </div>
                ))}
                {images.length < 8 && (
                  <label className="aspect-square border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-pink transition-colors">
                    <Upload size={20} className="text-muted mb-1" />
                    <span className="text-xs text-muted">Add image</span>
                    <input type="file" accept="image/*" multiple className="hidden"
                      onChange={e => setImages(imgs => [...imgs, ...Array.from(e.target.files)].slice(0, 8))} />
                  </label>
                )}
              </div>
              <p className="text-xs text-muted mt-2">Up to 8 images. First image will be the cover.</p>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Details */}
      {tab === 'details' && (
        <Card>
          <CardHeader><span className="font-bold text-sm">Fabric Specifications</span></CardHeader>
          <CardBody className="space-y-3">
            <div className="grid sm:grid-cols-3 gap-3">
              <input className="input" type="number" placeholder="GSM" value={form.gsm} onChange={set('gsm')} />
              <input className="input" type="number" placeholder="Width (inches)" value={form.width_inches} onChange={set('width_inches')} />
              <input className="input" placeholder="Weave type" value={form.weave_type} onChange={set('weave_type')} />
            </div>
            <textarea className="input w-full h-20" placeholder="Care instructions" value={form.care_instructions} onChange={set('care_instructions')} />
            <div className="flex gap-6 text-sm flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.is_sample_available} onChange={set('is_sample_available')} /> Sample available</label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.is_wholesale_only} onChange={set('is_wholesale_only')} /> Wholesale only</label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.is_gi_tagged} onChange={set('is_gi_tagged')} /> GI Tagged</label>
            </div>
            {form.is_gi_tagged && <input className="input w-full" placeholder="GI tag name (e.g. Kanjivaram Silk)" value={form.gi_tag_name} onChange={set('gi_tag_name')} />}
          </CardBody>
        </Card>
      )}

      {/* Specifications */}
      {tab === 'specs' && (
        <div className="space-y-4">
          <Card>
            <CardHeader><span className="font-bold text-sm">Physical Properties</span></CardHeader>
            <CardBody className="space-y-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <div><label className="text-xs text-muted">Finish Type</label><select className="input w-full" value={form.finish_type} onChange={set('finish_type')}><option value="">Select...</option>{FINISH_TYPES.map(f => <option key={f} value={f}>{f}</option>)}</select></div>
                <div><label className="text-xs text-muted">Shrinkage (%)</label><input className="input w-full" type="number" step="0.1" placeholder="e.g. 3.5" value={form.shrinkage_pct} onChange={set('shrinkage_pct')} /></div>
                <div><label className="text-xs text-muted">Pilling Grade (1-5)</label><input className="input w-full" type="number" min="1" max="5" placeholder="ISO 12945" value={form.pilling_grade} onChange={set('pilling_grade')} /></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><label className="text-xs text-muted">Color Fastness</label><input className="input w-full" placeholder="e.g. Grade 4" value={form.color_fastness} onChange={set('color_fastness')} /></div>
                <div><label className="text-xs text-muted">Tensile Strength (N/cm²)</label><input className="input w-full" type="number" step="0.1" value={form.tensile_strength} onChange={set('tensile_strength')} /></div>
                <div><label className="text-xs text-muted">Weight per metre (g)</label><input className="input w-full" type="number" step="0.01" value={form.weight_per_metre} onChange={set('weight_per_metre')} /></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><label className="text-xs text-muted">Drape</label><select className="input w-full" value={form.drape_coefficient} onChange={set('drape_coefficient')}><option value="">Select...</option>{DRAPE_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}</select></div>
                <div><label className="text-xs text-muted">Moisture Absorption (%)</label><input className="input w-full" type="number" step="0.1" value={form.moisture_absorption} onChange={set('moisture_absorption')} /></div>
                <div><label className="text-xs text-muted">Opacity</label><select className="input w-full" value={form.opacity} onChange={set('opacity')}><option value="">Select...</option>{OPACITY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}</select></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><label className="text-xs text-muted">Stretch (%)</label><input className="input w-full" type="number" step="0.1" placeholder="0 if no stretch" value={form.stretch_pct} onChange={set('stretch_pct')} /></div>
                <div><label className="text-xs text-muted">Stretch Direction</label><select className="input w-full" value={form.stretch_direction} onChange={set('stretch_direction')}><option value="">Select...</option>{STRETCH_DIRS.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader><span className="font-bold text-sm">Production & Craft</span></CardHeader>
            <CardBody className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><label className="text-xs text-muted">Dyeing Method</label><select className="input w-full" value={form.dyeing_method} onChange={set('dyeing_method')}><option value="">Select...</option>{DYEING_METHODS.map(d => <option key={d} value={d}>{d}</option>)}</select></div>
                <div><label className="text-xs text-muted">Printing Method</label><select className="input w-full" value={form.printing_method} onChange={set('printing_method')}><option value="">Select...</option>{PRINTING_METHODS.map(p => <option key={p} value={p}>{p}</option>)}</select></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><label className="text-xs text-muted">Loom Type</label><select className="input w-full" value={form.loom_type} onChange={set('loom_type')}><option value="">Select...</option>{LOOM_TYPES.map(l => <option key={l} value={l}>{l}</option>)}</select></div>
                <div><label className="text-xs text-muted">Selvage Type</label><input className="input w-full" placeholder="e.g. Plain, Tucked, Fused" value={form.selvage_type} onChange={set('selvage_type')} /></div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader><span className="font-bold text-sm">Certification & Sustainability</span></CardHeader>
            <CardBody className="space-y-3">
              <div><label className="text-xs text-muted">Certification</label><select className="input w-full" value={form.certification} onChange={set('certification')}><option value="">Select...</option>{CERTIFICATIONS.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
              <div className="flex gap-6 text-sm flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.eco_friendly} onChange={set('eco_friendly')} /> Eco-friendly</label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.recyclable} onChange={set('recyclable')} /> Recyclable</label>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader><span className="font-bold text-sm">Usage & Season</span></CardHeader>
            <CardBody className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><label className="text-xs text-muted">Season</label><select className="input w-full" value={form.seasonal_suitability} onChange={set('seasonal_suitability')}><option value="">Select...</option>{SEASONS.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                <div><label className="text-xs text-muted">Recommended Use</label><select className="input w-full" value={form.recommended_use} onChange={set('recommended_use')}><option value="">Select...</option>{RECOMMENDED_USES.map(u => <option key={u} value={u}>{u}</option>)}</select></div>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Pricing */}
      {tab === 'pricing' && (
        <div className="space-y-4">
          <Card>
            <CardHeader><span className="font-bold text-sm">Pricing</span></CardHeader>
            <CardBody className="space-y-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <div><label className="text-xs text-muted">Retail price/m *</label><div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted text-sm">₹</span><input className="input pl-6 w-full" type="number" value={form.retail_price} onChange={set('retail_price')} /></div></div>
                <div><label className="text-xs text-muted">Wholesale price/m</label><div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted text-sm">₹</span><input className="input pl-6 w-full" type="number" value={form.wholesale_price} onChange={set('wholesale_price')} /></div></div>
                <div><label className="text-xs text-muted">MRP/m</label><div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted text-sm">₹</span><input className="input pl-6 w-full" type="number" value={form.mrp} onChange={set('mrp')} /></div></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><label className="text-xs text-muted">GST rate (%)</label><select className="input w-full" value={form.gst_rate} onChange={set('gst_rate')}>{[0,5,12,18,28].map(r => <option key={r} value={r}>{r}%</option>)}</select></div>
                <div><label className="text-xs text-muted">Min retail order (m)</label><input className="input w-full" type="number" value={form.min_order_retail} onChange={set('min_order_retail')} /></div>
                <div><label className="text-xs text-muted">Min wholesale order (m)</label><input className="input w-full" type="number" value={form.min_order_wholesale} onChange={set('min_order_wholesale')} /></div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <span className="font-bold text-sm">Bulk Pricing Tiers</span>
              <Button size="sm" variant="secondary" onClick={() => setTiers(t => [...t, { ...TIER_INIT }])}><Plus size={12} /> Add Tier</Button>
            </CardHeader>
            <CardBody className="space-y-3">
              {tiers.length === 0 && <p className="text-xs text-muted text-center py-3">No bulk tiers. Add tiers for quantity discounts.</p>}
              {tiers.map((tier, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input className="input flex-1" type="number" placeholder="Min metres" value={tier.min_metres} onChange={e => setTiers(ts => ts.map((t, j) => j === i ? { ...t, min_metres: e.target.value } : t))} />
                  <input className="input flex-1" type="number" placeholder="Max metres (opt)" value={tier.max_metres} onChange={e => setTiers(ts => ts.map((t, j) => j === i ? { ...t, max_metres: e.target.value } : t))} />
                  <input className="input flex-1" type="number" placeholder="₹/metre" value={tier.price_per_metre} onChange={e => setTiers(ts => ts.map((t, j) => j === i ? { ...t, price_per_metre: e.target.value } : t))} />
                  <button onClick={() => setTiers(ts => ts.filter((_, j) => j !== i))} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 size={14} className="text-red-400" /></button>
                </div>
              ))}
            </CardBody>
          </Card>
        </div>
      )}

      {/* Variants */}
      {tab === 'variants' && (
        <Card>
          <CardHeader>
            <span className="font-bold text-sm">Colour Variants</span>
            <Button size="sm" variant="secondary" onClick={() => setVariants(v => [...v, { ...VARIANT_INIT }])}><Plus size={12} /> Add Variant</Button>
          </CardHeader>
          <CardBody className="space-y-3">
            {variants.map((v, i) => (
              <div key={i} className="flex gap-2 items-center flex-wrap">
                <input type="color" value={v.color_hex || '#cccccc'} onChange={e => setVariants(vs => vs.map((vv, j) => j === i ? { ...vv, color_hex: e.target.value } : vv))} className="w-10 h-10 rounded-lg border border-border cursor-pointer" title="Pick colour" />
                <input className="input flex-1" placeholder="Colour name *" value={v.color_name} onChange={e => setVariants(vs => vs.map((vv, j) => j === i ? { ...vv, color_name: e.target.value } : vv))} />
                <input className="input flex-1" placeholder="SKU" value={v.sku} onChange={e => setVariants(vs => vs.map((vv, j) => j === i ? { ...vv, sku: e.target.value } : vv))} />
                <input className="input w-28" type="number" placeholder="Stock (m)" value={v.stock_metres} onChange={e => setVariants(vs => vs.map((vv, j) => j === i ? { ...vv, stock_metres: e.target.value } : vv))} />
                {variants.length > 1 && <button onClick={() => setVariants(vs => vs.filter((_, j) => j !== i))} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 size={14} className="text-red-400" /></button>}
              </div>
            ))}
          </CardBody>
        </Card>
      )}

      {/* SEO */}
      {tab === 'seo' && (
        <Card>
          <CardHeader><span className="font-bold text-sm">SEO Metadata</span></CardHeader>
          <CardBody className="space-y-3">
            <input className="input w-full" placeholder="Meta title (max 120 chars)" value={form.meta_title} onChange={set('meta_title')} maxLength={120} />
            <textarea className="input w-full h-20" placeholder="Meta description (max 300 chars)" value={form.meta_description} onChange={set('meta_description')} maxLength={300} />
            <p className="text-xs text-muted">Leave blank to auto-generate from product name and description.</p>
          </CardBody>
        </Card>
      )}

      <div className="mt-6 flex gap-2">
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : isEdit ? 'Update Product' : 'Create Product'}
        </Button>
        <Button variant="secondary" onClick={() => navigate('/supplier/products')}>Cancel</Button>
      </div>
    </div>
  )
}
